import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../core/services/notification.service';
import { SettingsService } from '../../../core/services/settings.service';
import { ConsultationPricingItemDto } from '../../../core/models/consultation-pricing-item.model';

interface ConsultationPricingDraft {
  serviceItem: string;
  cost: string;
}

@Component({
  selector: 'app-pricing-settings',
  imports: [CommonModule, FormsModule],
  templateUrl: './pricing-settings.html',
  styleUrl: './pricing-settings.scss'
})
export class PricingSettings implements OnInit {

  isLoading = false;
  isSaving = false;
  errorMessage: string | null = null;
  statusMessage: string | null = null;

  pricingItems: ConsultationPricingItemDto[] = [];

  draftItem: ConsultationPricingDraft = {
    serviceItem: '',
    cost: ''
  };

  constructor(
    private settingsService: SettingsService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadPricingItems();
  }

  get totalServices(): number {
    return this.pricingItems.length;
  }

  get averageCost(): number {
    if (this.pricingItems.length === 0) {
      return 0;
    }

    const totalCost = this.pricingItems.reduce((sum, item) => sum + item.cost, 0);
    return totalCost / this.pricingItems.length;
  }

  get highestCost(): number {
    if (this.pricingItems.length === 0) {
      return 0;
    }

    return Math.max(...this.pricingItems.map(item => item.cost));
  }

  addPricingItem(): void {
    this.errorMessage = null;
    this.statusMessage = null;

    const serviceItem = this.draftItem.serviceItem.trim();
    const cost = Number(this.draftItem.cost);

    if (!serviceItem) {
      this.errorMessage = 'Enter a service item name before adding it.';
      return;
    }

    if (Number.isNaN(cost) || cost < 0) {
      this.errorMessage = 'Enter a valid service cost.';
      return;
    }

    const pricingItem: ConsultationPricingItemDto = {
      id: this.generateId(),
      serviceItem,
      cost
    };

    this.pricingItems = [...this.pricingItems, pricingItem];
    this.draftItem = {
      serviceItem: '',
      cost: ''
    };

    this.statusMessage = `${serviceItem} added to the pricing list.`;
  }

  removePricingItem(index: number): void {
    this.errorMessage = null;
    this.statusMessage = null;

    const removedItem = this.pricingItems[index];
    this.pricingItems = this.pricingItems.filter((_, itemIndex) => itemIndex !== index);

    if (removedItem) {
      this.statusMessage = `${removedItem.serviceItem} removed from the pricing list.`;
    }
  }

  savePricingItems(showMessage = true): void {
    this.isSaving = true;
    this.errorMessage = null;

    this.settingsService.saveConsultationPricingItems(this.pricingItems).subscribe({
      next: (items) => {
        this.pricingItems = items ?? [];

        if (showMessage) {
          this.notificationService.success('Consultation pricing saved successfully.');
          this.statusMessage = 'Pricing items saved for future invoice generation.';
        }

        this.isSaving = false;
      },
      error: (error) => {
        this.errorMessage = 'Failed to save consultation pricing: ' + (error?.error?.message || error.message || 'Unknown error');
        this.notificationService.error(this.errorMessage);
        this.isSaving = false;
      }
    });
  }

  trackPricingItem(index: number, item: ConsultationPricingItemDto): string {
    return item.id ?? index.toString();
  }

  formatCurrency(amount: number): string {
    return amount.toLocaleString('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  private loadPricingItems(): void {
    this.isLoading = true;

    this.settingsService.getConsultationPricingItems().subscribe({
      next: (items) => {
        this.pricingItems = items ?? [];
        this.isLoading = false;
      },
      error: (error) => {
        this.pricingItems = [];
        this.errorMessage = 'Failed to load consultation pricing: ' + (error?.error?.message || error.message || 'Unknown error');
        this.notificationService.error(this.errorMessage);
        this.isLoading = false;
      }
    });
  }

  private generateId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }

    return `pricing-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

}
